/**
 * [rev16 Phase A] ReceiptDetailSection — read-only receipt block with action affordances
 *
 * Read-only presentation of a transaction's `receiptAttachment`. The section
 * renders one of two layouts:
 *   - Empty state (`receipt-empty`) when no receipt is attached; the "Attach
 *     Receipt" button uses the `onReplace` callback so the same widget serves
 *     both the attach and the replace flows.
 *   - Populated state (`receipt-detail`) with view, replace, and remove
 *     affordances. The buttons are pure event hooks — the host owns the
 *     upload/preview/confirmation logic.
 *
 * The prop shape is derived from the current `Transaction.receiptAttachment`
 * type so the contract stays in lock-step with the wire schema. No field is
 * invented here.
 *
 * Every interactive control carries an explicit `type="button"`, an
 * `aria-label`, and a 44px footprint so the section is mobile-first and
 * keyboard-accessible.
 *
 * Stable test IDs:
 *   - `data-testid="receipt-empty"` — empty-state row.
 *   - `data-testid="receipt-detail"` — populated row.
 *   - `data-testid="receipt-view-btn"` — view affordance.
 *   - `data-testid="receipt-replace-btn"` — replace / attach affordance.
 *   - `data-testid="receipt-remove-btn"` — remove affordance.
 *
 * @see docs/superpowers/plans/2026-08-16-rev16-modal-redesign-plan.md (Section 4.4)
 */
import * as React from 'react';
import { Paperclip } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { Transaction } from '@/lib/types';

/**
 * Receipt attachment shape, derived from the existing `Transaction` type.
 * Using `NonNullable<Transaction['receiptAttachment']>` instead of a hand-rolled
 * shape guarantees the contract follows any future wire-schema change.
 */
export type ReceiptAttachmentSummary = NonNullable<Transaction['receiptAttachment']>;

export interface ReceiptDetailSectionProps {
  /** The receipt attachment, or `null` when none is attached. */
  receipt: ReceiptAttachmentSummary | null;
  /** Bytes-to-readable-string helper invoked as `fmtSize(receipt.size)`. */
  fmtSize: (bytes: number) => string;
  /** Tapped when the user wants to view the receipt (open preview). */
  onView?: () => void;
  /** Tapped when the user wants to attach or replace a receipt. */
  onReplace?: () => void;
  /** Tapped when the user wants to remove the existing receipt. */
  onRemove?: () => void;
}

/** Shared button className — 44px touch target, accessible focus ring. */
const ACTION_BUTTON_CLASS = cn(
  'inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3 text-xs font-medium rounded-md',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:opacity-50 disabled:cursor-not-allowed',
);

export function ReceiptDetailSection(
  props: ReceiptDetailSectionProps,
): React.ReactElement {
  const { receipt, fmtSize, onView, onReplace, onRemove } = props;

  if (!receipt) {
    return (
      <div
        data-testid="receipt-empty"
        className="flex flex-wrap items-center gap-3 border-b border-border py-3"
      >
        <Paperclip
          className="h-[18px] w-[18px] text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <span className="min-w-0 break-words text-xs font-semibold text-foreground">
          Receipt
        </span>
        <span className="flex-1 min-w-0 break-words text-xs text-muted-foreground italic">No receipt attached</span>
        <button
          type="button"
          data-testid="receipt-replace-btn"
          aria-label="Attach receipt"
          onClick={onReplace}
          disabled={!onReplace}
          className={cn(ACTION_BUTTON_CLASS, 'text-blue-600 hover:text-blue-800 hover:bg-blue-50')}
        >
          <Paperclip
            className="h-3.5 w-3.5 mr-1"
            aria-hidden="true"
          />
          Attach
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="receipt-detail"
      className="flex flex-wrap items-center gap-3 border-b border-border py-3"
    >
      <Paperclip
        className="h-[18px] w-[18px] text-muted-foreground shrink-0"
        aria-hidden="true"
      />
      <span className="min-w-0 break-words text-xs font-semibold text-foreground">
        Receipt
      </span>
      <button
        type="button"
        data-testid="receipt-view-btn"
        aria-label={`View receipt ${receipt.filename}`}
        onClick={onView}
        disabled={!onView}
        className={cn(
          ACTION_BUTTON_CLASS,
          'flex-1 min-w-0 justify-start text-left px-2 hover:bg-slate-50',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">
            {receipt.filename}
          </span>
          <span className="block text-xs text-muted-foreground tabular-nums">
            {fmtSize(receipt.size)} · {receipt.mimetype}
          </span>
        </span>
      </button>
      <button
        type="button"
        data-testid="receipt-replace-btn"
        aria-label="Replace receipt"
        onClick={onReplace}
        disabled={!onReplace}
        className={cn(ACTION_BUTTON_CLASS, 'text-blue-600 hover:text-blue-800 hover:bg-blue-50')}
      >
        Replace
      </button>
      <button
        type="button"
        data-testid="receipt-remove-btn"
        aria-label="Remove receipt"
        onClick={onRemove}
        disabled={!onRemove}
        className={cn(ACTION_BUTTON_CLASS, 'text-red-600 hover:text-red-800 hover:bg-red-50')}
      >
        Remove
      </button>
    </div>
  );
}

export default ReceiptDetailSection;
